import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard } from "@/components/common";
import { CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";

export default function BillingPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState({});
  const [commissions, setCommissions] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, p, c] = await Promise.all([
        api.get("/billing/status"),
        api.get("/billing/plans"),
        api.get("/billing/commissions").catch(() => ({ data: [] })),
      ]);
      setStatus(s.data); setPlans(p.data); setCommissions(c.data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  // Poll checkout status when redirected back from Stripe (?session_id=...).
  const pollStatus = async (sessionId, attempts = 0) => {
    const max = 8, intervalMs = 2000;
    if (attempts >= max) { setErr("Payment status check timed out — please refresh."); return; }
    try {
      const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
      if (data.payment_status === "paid") { await load(); return; }
      if (data.status === "expired") { setErr("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), intervalMs);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (sid) pollStatus(sid);
  }, []);

  const checkout = async (tier) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_tier: tier,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  const portal = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.get("/billing/portal"); window.location.href = data.url; }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); setBusy(false); }
  };
  const sync = async () => { setBusy(true); try { await api.post("/billing/sync"); await load(); } finally { setBusy(false); } };

  if (!status) return <div style={{ color: "var(--ink-mute)" }}>Loading…</div>;

  const sub = status.subscription || {};
  const inactive = status.effective_mode === "off";

  return (
    <>
      <PageHeader title="Billing & Subscription"
        subtitle={inactive
          ? "Monetization is currently disabled platform-wide. You can browse plans below — checkout will activate once an admin turns billing on."
          : `Your effective mode: ${status.effective_mode}.`} />

      <div className="card" style={{ padding: 16, marginBottom: 24, borderLeft: "3px solid var(--ink-mute)", display: "flex", gap: 12, alignItems: "flex-start" }} data-testid="platform-vs-trade-banner">
        <ShieldCheck size={18} color="var(--ink-mute)" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>
          <b style={{ color: "var(--ink)" }}>Platform fees stay separate from trade payments.</b> What you see here is the SaaS subscription paid TO the platform. Buyer↔supplier trade is settled directly between the two parties — the platform never holds, splits, or routes those funds.
        </div>
      </div>

      {inactive && (
        <div className="card" style={{ padding: 16, marginBottom: 24, borderLeft: "3px solid var(--amber-500)", display: "flex", gap: 12 }}>
          <AlertCircle size={18} color="var(--amber-500)" />
          <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>Billing is in <b style={{ color: "var(--amber-400)" }}>dormant mode</b>. Plan quotas, subscription requirements and commissions are not enforced. Admin can flip them on at any time.</div>
        </div>
      )}

      {user.role === "supplier" && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
            <KpiCard label="Current plan" value={(sub.plan_tier || "free").toUpperCase()} accent="var(--amber-400)" />
            <KpiCard label="Status" value={(sub.status || "inactive").replace(/_/g, " ")} />
            <KpiCard label="Override" value={sub.manual_override ? "YES" : "—"} sub={sub.override_reason || ""} />
            <KpiCard label="Mode" value={status.effective_mode.toUpperCase()} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 24 }}>
            {Object.entries(plans).map(([key, p]) => (
              <div key={key} className="card" style={{ padding: 24, position: "relative", borderColor: sub.plan_tier === key ? "var(--amber-500)" : undefined }}>
                {sub.plan_tier === key && <span className="pill pill-accepted" style={{ position: "absolute", top: 14, right: 14 }}>CURRENT</span>}
                <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)" }}>{key.toUpperCase()}</div>
                <div className="font-serif-display" style={{ fontSize: 28, fontWeight: 600, marginTop: 8 }}>{p.name}</div>
                <div style={{ marginTop: 6 }}><span className="font-serif-display" style={{ fontSize: 36, color: "var(--amber-500)", fontWeight: 600 }}>${p.price_cad}</span><span style={{ color: "var(--ink-mute)", fontSize: 13 }}> CAD/mo</span></div>
                <ul style={{ listStyle: "none", padding: 0, margin: "20px 0", display: "grid", gap: 8 }}>
                  {p.features.map(f => (
                    <li key={f} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "var(--ink-dim)" }}>
                      <CheckCircle2 size={14} color="var(--emerald)" /> {f}
                    </li>
                  ))}
                </ul>
                {key !== "free" && (
                  <button
                    data-testid={`subscribe-${key}`}
                    onClick={() => checkout(key)}
                    disabled={busy || inactive || sub.plan_tier === key}
                    className="btn btn-primary"
                    style={{ width: "100%", justifyContent: "center" }}>
                    {sub.plan_tier === key ? "Active" : inactive ? "Disabled (dormant)" : "Subscribe →"}
                  </button>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
            {sub.stripe_customer_id && <button className="btn btn-ghost" onClick={portal} disabled={busy || inactive}>Manage in Stripe Portal</button>}
            <button className="btn btn-ghost" onClick={sync} disabled={busy}>Refresh status</button>
          </div>
        </>
      )}

      <div className="card">
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }} className="font-mono">
          <span style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)" }}>COMMISSIONS · {commissions.length}</span>
          {!status.monetization_enabled && <span style={{ marginLeft: 12, fontSize: 11, color: "var(--amber-400)" }}>(commission tracking is off)</span>}
        </div>
        {commissions.length === 0 ? <div style={{ padding: 32, textAlign: "center", color: "var(--ink-mute)" }}>No commissions recorded yet.</div> : (
          <table className="data">
            <thead><tr><th>Order</th><th>Rate</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {commissions.map(c => (
                <tr key={c.id}>
                  <td className="font-mono" style={{ color: "var(--amber-400)" }}>{c.order_code}</td>
                  <td>{c.rate_pct}%</td>
                  <td style={{ fontWeight: 600 }}>${c.amount.toFixed(2)}</td>
                  <td><span className="pill pill-pending">{c.status}</span></td>
                  <td className="font-mono" style={{ color: "var(--ink-mute)", fontSize: 12 }}>{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {err && <div style={{ color: "var(--red)", marginTop: 12 }}>{err}</div>}
    </>
  );
}
